import { useState } from 'react';
import { Building2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useBalanceSheet } from '@/hooks/useAccounting';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatCurrency } from '@/lib/utils';

function today() { return new Date().toISOString().slice(0, 10); }

export function BalanceSheetPage() {
  const [asOf, setAsOf] = useState<string>(today);
  const { data, isLoading, error } = useBalanceSheet(asOf);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load balance sheet"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  if (!data || (data.assets.length === 0 && data.liabilities.length === 0 && data.equity.length === 0)) {
    return (
      <Card>
        <EmptyState
          icon={Building2}
          title="No asset, liability, or equity activity"
          description="Once accounts in these categories have activity, this statement will populate."
        />
      </Card>
    );
  }

  const balanceDiff = data.totalAssets - (data.totalLiabilities + data.totalEquity + data.retainedEarnings);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1">Balance Sheet</div>
          <div className="text-sm text-text-muted">
            Snapshot of financial position. Assets = Liabilities + Equity.
          </div>
        </div>
        <div className="w-56">
          <Input label="As of" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </div>
      </div>

      {/* Balance check */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="kicker mb-1">Accounting Equation</div>
            <div className="text-sm text-text-muted font-mono">
              Assets = Liabilities + Equity
            </div>
          </div>
          {data.balanced ? (
            <Badge tone="success"><CheckCircle2 size={10} /> Balanced</Badge>
          ) : (
            <Badge tone="danger">Off by {formatCurrency(Math.abs(balanceDiff))}</Badge>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assets */}
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border bg-bg-subtle">
            <div className="kicker">Assets</div>
          </header>
          {data.assets.length === 0 ? (
            <div className="p-6 text-sm text-text-dim text-center">No asset balances.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.assets.map((a) => (
                  <tr key={a.accountId} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                    <td className="px-6 py-3 font-mono text-brass-300 w-24">{a.accountNumber}</td>
                    <td className="px-6 py-3 text-text">{a.accountName}</td>
                    <td className="px-6 py-3 text-right tabular text-text">{formatCurrency(a.amount)}</td>
                  </tr>
                ))}
                <tr className="border-t border-border-strong bg-bg-panel">
                  <td colSpan={2} className="px-6 py-3 text-right">
                    <span className="kicker">Total Assets</span>
                  </td>
                  <td className="px-6 py-3 text-right tabular font-display text-lg text-brass-300">
                    {formatCurrency(data.totalAssets)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </Card>

        {/* Liabilities + Equity */}
        <div className="space-y-6">
          <Card className="p-0 overflow-hidden">
            <header className="px-6 py-4 border-b border-border bg-bg-subtle">
              <div className="kicker">Liabilities</div>
            </header>
            {data.liabilities.length === 0 ? (
              <div className="p-6 text-sm text-text-dim text-center">No liabilities.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.liabilities.map((l) => (
                    <tr key={l.accountId} className="border-b border-border last:border-0 hover:bg-bg-hover/40">
                      <td className="px-6 py-3 font-mono text-brass-300 w-24">{l.accountNumber}</td>
                      <td className="px-6 py-3 text-text">{l.accountName}</td>
                      <td className="px-6 py-3 text-right tabular text-text">{formatCurrency(l.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-border-strong bg-bg-panel">
                    <td colSpan={2} className="px-6 py-3 text-right">
                      <span className="kicker">Total Liabilities</span>
                    </td>
                    <td className="px-6 py-3 text-right tabular font-medium text-text">
                      {formatCurrency(data.totalLiabilities)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </Card>

          <Card className="p-0 overflow-hidden">
            <header className="px-6 py-4 border-b border-border bg-bg-subtle">
              <div className="kicker">Equity</div>
            </header>
            <table className="w-full text-sm">
              <tbody>
                {data.equity.map((e) => (
                  <tr key={e.accountId} className="border-b border-border hover:bg-bg-hover/40">
                    <td className="px-6 py-3 font-mono text-brass-300 w-24">{e.accountNumber}</td>
                    <td className="px-6 py-3 text-text">{e.accountName}</td>
                    <td className="px-6 py-3 text-right tabular text-text">{formatCurrency(e.amount)}</td>
                  </tr>
                ))}
                {data.retainedEarnings !== 0 && (
                  <tr className="border-b border-border hover:bg-bg-hover/40">
                    <td className="px-6 py-3 font-mono text-text-dim w-24">—</td>
                    <td className="px-6 py-3 text-text italic">Retained Earnings (YTD)</td>
                    <td className="px-6 py-3 text-right tabular text-text">{formatCurrency(data.retainedEarnings)}</td>
                  </tr>
                )}
                <tr className="border-t border-border-strong bg-bg-panel">
                  <td colSpan={2} className="px-6 py-3 text-right">
                    <span className="kicker">Total Equity</span>
                  </td>
                  <td className="px-6 py-3 text-right tabular font-medium text-text">
                    {formatCurrency(data.totalEquity + data.retainedEarnings)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>

          <div className="bg-bg-panel border-2 border-brass-400/40 rounded-sm p-5">
            <div className="flex items-center justify-between">
              <span className="kicker">Total Liabilities + Equity</span>
              <span className="font-display text-lg tabular text-brass-300">
                {formatCurrency(data.totalLiabilities + data.totalEquity + data.retainedEarnings)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
